import React from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Tag, Calendar, User, Clock, Share2, ChevronLeft, Facebook, Twitter, Linkedin } from 'lucide-react';
import { useEffect } from 'react';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  category: string;
  author: string;
  authorImage: string;
  authorRole: string;
  date: string;
  readTime: string;
  tags: string[];
}

const posts: Record<number, BlogPost> = {
  1: {
    id: 1,
    title: 'Как выбрать надежного поставщика в Китае: пошаговое руководство',
    excerpt: 'Подробный гайд по поиску и проверке китайских поставщиков. Узнайте, как избежать распространенных ошибок и построить надежные деловые отношения.',
    content: `
      <h2>Введение</h2>
      <p>Поиск надежного поставщика в Китае – это ключевой этап в построении успешного бизнеса с китайскими партнерами. В этой статье мы рассмотрим основные шаги, которые помогут вам найти и проверить поставщиков, а также установить с ними долгосрочные деловые отношения.</p>

      <h2>1. Начальный поиск поставщиков</h2>
      <p>Первый шаг в поиске поставщика – это определение потенциальных партнеров. Существует несколько основных источников:</p>
      <ul>
        <li>Торговые площадки (Alibaba, Global Sources)</li>
        <li>Отраслевые выставки в Китае</li>
        <li>Рекомендации от других импортеров</li>
        <li>Специализированные B2B платформы</li>
      </ul>

      <h2>2. Проверка поставщиков</h2>
      <p>После составления списка потенциальных поставщиков необходимо провести их тщательную проверку:</p>
      <ul>
        <li>Запросить бизнес-лицензию и другие документы</li>
        <li>Проверить опыт работы и историю компании</li>
        <li>Изучить отзывы других клиентов</li>
        <li>Запросить образцы продукции</li>
      </ul>

      <h2>3. Коммуникация и переговоры</h2>
      <p>Правильная коммуникация с китайскими поставщиками имеет решающее значение. Вот несколько важных аспектов:</p>
      <ul>
        <li>Четко формулируйте свои требования</li>
        <li>Используйте профессиональных переводчиков</li>
        <li>Обсуждайте все детали заранее</li>
        <li>Фиксируйте договоренности в письменном виде</li>
      </ul>

      <h2>4. Заключение контракта</h2>
      <p>При заключении контракта обратите внимание на следующие пункты:</p>
      <ul>
        <li>Условия оплаты и поставки</li>
        <li>Требования к качеству продукции</li>
        <li>Сроки производства и доставки</li>
        <li>Гарантийные обязательства</li>
      </ul>

      <h2>Заключение</h2>
      <p>Выбор надежного поставщика в Китае – это сложный, но важный процесс, который требует тщательного подхода и внимания к деталям. Следуя этим рекомендациям, вы сможете минимизировать риски и найти надежного партнера для своего бизнеса.</p>
    `,
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8',
    category: 'Бизнес',
    author: 'Анна Смирнова',
    authorImage: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330',
    authorRole: 'Эксперт по работе с китайскими поставщиками',
    date: '15 марта 2024',
    readTime: '8 мин',
    tags: ['поставщики', 'бизнес', 'китай']
  }
};

function BlogArticle() {
  const { id } = useParams<{ id: string }>();
  const post = posts[Number(id)];
  
  useEffect(() => {
    // Preload the social sharing image
    if (post?.image) {
      const img = new Image();
      img.src = `${post.image}?auto=format&fit=crop&q=80&w=1200&h=630`;
    }
    
    // Pre-encode sharing URLs
    if (post) {
      const urls = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://kitlin.ru/blog/${post.id}`)}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://kitlin.ru/blog/${post.id}`)}&text=${encodeURIComponent(post.title)}`,
        linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://kitlin.ru/blog/${post.id}`)}&title=${encodeURIComponent(post.title)}&summary=${encodeURIComponent(post.excerpt)}`
      };
      
      // Store pre-encoded URLs
      Object.entries(urls).forEach(([platform, url]) => {
        localStorage.setItem(`share_url_${post.id}_${platform}`, url);
      });
    }
  }, [post]);
  
  const handleShare = (platform: string) => {
    if (!post) return;
    
    // Use pre-encoded URL from localStorage
    const url = localStorage.getItem(`share_url_${post.id}_${platform}`);
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-white pt-32 px-6">
        <div className="max-w-[980px] mx-auto text-center">
          <h1 className="text-2xl font-semibold text-[#1d1d1f]">Статья не найдена</h1>
          <a 
            href="/blog" 
            className="inline-flex items-center text-[#0171E3] mt-4 hover:text-[#0156aa]"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Вернуться к списку статей
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{post.title} | KiTLiN Блог</title>
        <meta name="description" content={post.excerpt} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://kitlin.ru/blog/${post.id}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={`${post.image}?auto=format&fit=crop&q=80&w=1200&h=630`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:published_time" content="2024-03-15T10:00:00+03:00" />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map(tag => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={`https://kitlin.ru/blog/${post.id}`} />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={`${post.image}?auto=format&fit=crop&q=80&w=1200&h=630`} />
        
        {/* Structured Data for Article */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.image,
            "author": {
              "@type": "Person",
              "name": post.author,
              "jobTitle": post.authorRole
            },
            "publisher": {
              "@type": "Organization",
              "name": "KiTLiN",
              "logo": {
                "@type": "ImageObject",
                "url": "https://i.ibb.co/nj9SZYb/Kitlin-3.png"
              }
            },
            "datePublished": "2024-03-15T10:00:00+03:00",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://kitlin.ru/blog/${post.id}`
            }
          })}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6 bg-gradient-to-b from-[#fbfbfd] to-white">
        <div className="max-w-[980px] mx-auto">
          <a 
            href="/blog" 
            className="inline-flex items-center text-[#0171E3] mb-8 hover:text-[#0156aa]"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Вернуться к списку статей
          </a>
          
          <div className="flex items-center gap-4 text-sm text-[#86868b] mb-4">
            <span className="flex items-center">
              <Tag className="w-4 h-4 mr-1" />
              {post.category}
            </span>
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {post.date}
            </span>
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {post.readTime}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-semibold text-[#1d1d1f] mb-6 tracking-tight">
            {post.title}
          </h1>
          
          <p className="text-xl text-[#86868b] max-w-3xl leading-relaxed mb-8">
            {post.excerpt}
          </p>

          {/* Author Info */}
          <div className="flex items-center gap-4">
            <img 
              src={post.authorImage} 
              alt={post.author}
              className="w-12 h-12 rounded-full object-cover"
            />
            <div>
              <div className="font-medium text-[#1d1d1f]">{post.author}</div>
              <div className="text-sm text-[#86868b]">{post.authorRole}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-12 px-6">
        <div className="max-w-[780px] mx-auto">
          {/* Featured Image */}
          <div className="rounded-2xl overflow-hidden mb-12">
            <img 
              src={post.image}
              alt={post.title}
              className="w-full h-[400px] object-cover"
            />
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Tags and Share */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-[#f5f5f7] text-[#86868b] rounded-full text-sm"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
              
              <div className="flex items-center gap-4">
                <span className="text-sm text-[#86868b]">
                  <Share2 className="w-4 h-4 inline-block mr-2" />
                  Поделиться:
                </span>
                <div className="flex gap-2">
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleShare('facebook');
                    }}
                    className="p-2 rounded-full hover:bg-[#f5f5f7] transition-colors"
                    aria-label="Поделиться в Facebook"
                  >
                    <Facebook className="w-5 h-5 text-[#86868b]" />
                  </a>
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleShare('twitter');
                    }}
                    className="p-2 rounded-full hover:bg-[#f5f5f7] transition-colors"
                    aria-label="Поделиться в Twitter"
                  >
                    <Twitter className="w-5 h-5 text-[#86868b]" />
                  </a>
                  <a 
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleShare('linkedin');
                    }}
                    className="p-2 rounded-full hover:bg-[#f5f5f7] transition-colors"
                    aria-label="Поделиться в LinkedIn"
                  >
                    <Linkedin className="w-5 h-5 text-[#86868b]" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BlogArticle;